import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

// Aba "Segurança" das Configurações (SEF-70): lista as sessões ativas do
// usuário, permite encerrar uma sessão específica ou todas de uma vez.
// Spec visual pendente de alinhamento com UX (segue o padrão visual atual
// das Configurações; ver frontend/todo.md).

function formatarData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SegurancaSessoes() {
  const [sessoes, setSessoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [acaoEmAndamento, setAcaoEmAndamento] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const r = await apiFetch('/api/auth/sessoes');
      setSessoes(r?.sessoes || []);
    } catch {
      setErro('Não foi possível carregar as sessões ativas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const encerrarSessao = async (id) => {
    if (!window.confirm('Encerrar esta sessão?')) return;
    setAcaoEmAndamento(id);
    setErro('');
    try {
      await apiFetch(`/api/auth/sessoes/${id}`, { method: 'DELETE' });
      await carregar();
    } catch {
      setErro('Não foi possível encerrar a sessão.');
    } finally {
      setAcaoEmAndamento(null);
    }
  };

  const encerrarTodas = async () => {
    if (
      !window.confirm(
        'Encerrar todas as sessões? Você será desconectado em todos os dispositivos.',
      )
    ) {
      return;
    }
    setAcaoEmAndamento('todas');
    setErro('');
    try {
      await apiFetch('/api/auth/logout-all', { method: 'POST' });
      // O access token atual ainda vale até expirar, mas o refresh foi
      // revogado: força o fluxo de re-login.
      if (typeof window.onSessaoExpirada === 'function') {
        window.onSessaoExpirada();
      }
    } catch {
      setErro('Não foi possível encerrar todas as sessões.');
      setAcaoEmAndamento(null);
    }
  };

  return (
    <div className="seguranca-sessoes">
      <div className="templates-header">
        <h2>Sessões ativas</h2>
        <button
          className="btn btn-danger"
          onClick={encerrarTodas}
          disabled={acaoEmAndamento === 'todas' || sessoes.length === 0}
        >
          Encerrar todas
        </button>
      </div>

      <p className="descricao">
        Estes são os dispositivos com sessão aberta na sua conta. Encerre
        qualquer sessão que você não reconheça.
      </p>

      {erro && (
        <div className="login-error">
          <i className="fa-solid fa-circle-exclamation"></i>
          {erro}
        </div>
      )}

      {carregando ? (
        <p className="descricao">Carregando sessões...</p>
      ) : sessoes.length === 0 ? (
        <p className="descricao">Nenhuma sessão ativa encontrada.</p>
      ) : (
        <div className="templates-lista">
          {sessoes.map((s) => (
            <div key={s.id} className="template-item">
              <div className="template-info">
                <h3>
                  {s.userAgent || 'Dispositivo desconhecido'}
                  {s.atual && (
                    <span className="sessao-atual-badge"> (sessão atual)</span>
                  )}
                </h3>
                <p className="template-variaveis">IP: {s.ip || '—'}</p>
                <p className="template-variaveis">
                  Início: {formatarData(s.criadoEm)} · Último acesso:{' '}
                  {formatarData(s.ultimoAcessoEm)}
                </p>
              </div>
              <div className="template-acoes">
                <button
                  className="btn btn-danger"
                  onClick={() => encerrarSessao(s.id)}
                  disabled={s.atual || acaoEmAndamento === s.id}
                  title={
                    s.atual
                      ? 'A sessão atual não pode ser encerrada aqui — use Sair'
                      : undefined
                  }
                >
                  Encerrar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SegurancaSessoes;
